import { LogoutOutlined } from '@ant-design/icons';
import { Button, Divider, Flex, Menu, Popover } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import LanguageSwitcher from '@/components/auth/LanguageSwitcher';

import type { MenuProps } from 'antd';

type ProfileMenuPopupProps = {
  /** Popover trigger; receives whether the menu is open (for filled/link variants). */
  renderTrigger: (open: boolean) => ReactNode;
  menuItems: NonNullable<MenuProps['items']>;
  onMenuClick: MenuProps['onClick'];
  onLogout: () => void;
  extra?: ReactNode;
};

/**
 * Presentational profile popover. Call sites build menu items and trigger —
 * this kit does not import `@/features`.
 */
const ProfileMenuPopup = ({
  renderTrigger,
  menuItems,
  onMenuClick,
  onLogout,
  extra,
}: ProfileMenuPopupProps) => {
  const [t] = useTranslation();
  const [open, setOpen] = useState(false);

  const handleMenuClick: MenuProps['onClick'] = (info) => {
    onMenuClick?.(info);
    setOpen(false);
  };

  return (
    <Popover
      content={
        <Flex vertical gap="small">
          <Menu
            mode="vertical"
            selectable={false}
            items={menuItems}
            onClick={handleMenuClick}
          />
          <Divider size="small" />
          <LanguageSwitcher variant="dark" />
          {extra}
          <Button
            type="link"
            danger
            icon={<LogoutOutlined />}
            onClick={onLogout}
          >
            {t('LOGOUT')}
          </Button>
        </Flex>
      }
      trigger="click"
      placement="rightBottom"
      open={open}
      onOpenChange={setOpen}
      arrow={false}
      styles={{
        // Short action list; opt out of ConfigProvider popover maxHeight so Log out is not clipped.
        root: { maxHeight: 'none', overflow: 'visible' },
        content: { minWidth: 280 },
      }}
    >
      {renderTrigger(open)}
    </Popover>
  );
};

export default ProfileMenuPopup;
